import { HStack, IconButton, Td, Tooltip, Tr, useToast } from "@chakra-ui/react";
import { Fatura, Pix } from "../../utils/interfaces";
import { formatBRL } from "../../utils/currency";
import { abrirBoletoSicoobPdf, abrirFaturaPdf, abrirFaturaResumidaPdf } from "../../libs/axios";
import { BiSolidFilePdf, BiFile } from "react-icons/bi";
import { MdPix } from "react-icons/md";
import { FaBarcode } from "react-icons/fa6";
import { useState } from "react";

const status = {
  Vencida: "red.500",
  Aberta: "blue.500",
  Paga: "green.500",
  Fechada: "gray.500",
};

interface Props { 
  fatura: Fatura
  onPix: (fatura: Fatura, mode?: 'fatura' | 'parcial' | 'total_caixa', pix?: Pix) => void
}

export function Item({ fatura, onPix }: Props ) {
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const toast = useToast();
  const temBoletoSicoob = Boolean(fatura.boleto_sicoob_habilitado && (fatura.boleto_sicoob_id || fatura.boleto_sicoob));
  const valorPendente = Number(fatura.valor_pendente ?? fatura.valor_total);
  const pixesParciais = (fatura.pixes_parciais ?? []).filter((pix) => pix.status_label !== 'Pago' && pix.status_label !== 'Pago_Tardio');

  const abrirPdf = async (
    acao: () => Promise<void>,
    mensagemErro: string,
  ) => {
    if (isLoadingPdf) return;
    setIsLoadingPdf(true);
    try {
      await acao();
    } catch (e: any) {
      const isPopupBlocked = e instanceof Error && e.message === "popup_blocked";
      toast({
        title: isPopupBlocked ? "Popup bloqueado" : "Erro ao abrir PDF",
        description: isPopupBlocked ? "Permita pop-ups para abrir o PDF." : mensagemErro,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingPdf(false);
    }
  };

  const handlePdfResumido = () => abrirPdf(
    () => abrirFaturaResumidaPdf(fatura.id),
    "Não foi possível abrir a fatura resumida. Tente novamente em instantes.",
  );

  const handlePdfCompleto = () => abrirPdf(
    () => abrirFaturaPdf(fatura.id),
    "Não foi possível abrir a fatura completa. Tente novamente em instantes.",
  );

  const handleBoleto = async () => {
    if (isLoadingPdf || !fatura.boleto_sicoob_id) return;
    setIsLoadingPdf(true);
    try {
      await abrirBoletoSicoobPdf(fatura.boleto_sicoob_id);
    } catch (e: any) {
      const isPopupBlocked = e instanceof Error && e.message === "popup_blocked";
      toast({
        title: isPopupBlocked ? "Popup bloqueado" : "Erro ao abrir boleto",
        description: isPopupBlocked ? "Permita pop-ups para abrir o boleto." : "Tente novamente em instantes.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingPdf(false);
    }
  };
  return (
    <Tr p={0} className="group" key={`${fatura.id}-lista-relatorio`}>
      <Td p={0} textAlign={"center"} fontSize={"sm"}>
          {fatura.id}
      </Td>
      <Td p={0} textAlign={"center"} fontSize={"sm"}>
        {fatura.codigo}
      </Td>
      <Td
        fontWeight={"bold"}
        p={0}
        textAlign={"center"}
        color={status[fatura.status_label]}
        fontSize={"sm"}
      >
        {fatura.status_label}
      </Td>
      <Td p={0} textAlign={"center"} fontSize={"sm"}>
        {fatura.data_vencimento}
      </Td>
      <Td p={0} textAlign={"center"} fontSize={"sm"}>
        {fatura.created_at}
      </Td>
      <Td p={0} textAlign={"center"} fontSize={"sm"}>
        {formatBRL(Number(fatura.valor_total))}
      </Td>
      <Td p={0} textAlign={"center"} fontSize={"sm"}>
        <HStack justifyContent={"center"} spacing={2}>
          {temBoletoSicoob && (
            <Tooltip label="Abrir boleto Sicoob">
              <IconButton
                aria-label="abrir boleto Sicoob"
                icon={<FaBarcode size={"20px"} />}
                variant={"link"}
                colorScheme={"green"}
                onClick={handleBoleto}
                isLoading={isLoadingPdf}
              />
            </Tooltip>
          )}
          {(fatura.status_label !== 'Paga' && valorPendente > 0) && (
            <Tooltip label="Pagar com Pix">
              <IconButton
                icon={<MdPix size={'30px'} />}
                variant={'link'}
                aria-label="pix"
                onClick={() => onPix(fatura, 'fatura')}
              />
            </Tooltip>
          )}
          {fatura.status_label !== 'Paga' && pixesParciais.map((pix) => (
            <Tooltip key={pix.id} label={`PIX parcial ${formatBRL(Number(pix.valor ?? 0))}`}>
              <IconButton
                icon={<MdPix size={'30px'} />}
                variant={'link'}
                colorScheme="orange"
                aria-label={`pix parcial ${pix.id}`}
                onClick={() => onPix(fatura, 'parcial', pix)}
              />
            </Tooltip>
          ))}
          {(fatura.status_label !== 'Paga' && fatura.pix_total_caixa) && (
            <Tooltip label="Pagar PIX total caixa">
              <IconButton
                icon={<MdPix size={'30px'} />}
                variant={'link'}
                colorScheme="purple"
                aria-label="pix total caixa"
                onClick={() => onPix(fatura, 'total_caixa')}
              />
            </Tooltip>
          )}
        </HStack>
      </Td>
      <Td p={0} textAlign={"center"} fontSize={"sm"}>
        <HStack justifyContent={"center"} spacing={1}>
          <Tooltip label="Abrir fatura resumida">
            <IconButton
              aria-label="abrir fatura resumida"
              icon={<BiFile size={"28px"} />}
              variant={"link"}
              colorScheme={"purple"}
              onClick={handlePdfResumido}
              isLoading={isLoadingPdf}
            />
          </Tooltip>
          <Tooltip label="Abrir fatura completa">
            <IconButton
              aria-label="abrir fatura completa"
              icon={<BiSolidFilePdf size={"30px"} />}
              variant={"link"}
              colorScheme={"gray"}
              onClick={handlePdfCompleto}
              isLoading={isLoadingPdf}
            />
          </Tooltip>
        </HStack>
      </Td>
    </Tr>
  );
}
